/**
 * Bootstrap module - handles application startup sequence and initialization tasks
 */

import type { Config } from '../modules/config';
import { createLogger } from '../../shared/logger';
import {
  __getArchive as getImageArchive,
  __saveArchive as saveImageArchive,
} from '../modules/image';

const log = createLogger('portal:bootstrap');

/**
 * Inject adaptive favicons for light/dark mode if not already present
 */
export function injectAdaptiveFavicons(): void {
  try {
    const head = document.head;
    const hasAdaptive = head.querySelector('link[rel="icon"][media]');
    if (!hasAdaptive) {
      const makeLink = (href: string, media?: string) => {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.type = 'image/png';
        link.href = href;
        if (media) link.media = media;
        head.appendChild(link);
      };
      makeLink('/icons/favicon-light.png', '(prefers-color-scheme: light)');
      makeLink('/icons/favicon-dark.png', '(prefers-color-scheme: dark)');
      makeLink('/icons/ll-at-logo-light-icon128.png'); // fallback
    }
  } catch (err) {
    log.warn('Failed to inject adaptive favicons:', err);
  }
}

/**
 * Prune archived HiBid images from local storage
 * Removes cached images from hibid.com and image-proxy endpoints
 */
export function pruneHibidArchivedImages(): void {
  try {
    const map = getImageArchive();
    let changed = false;
    Object.keys(map || {}).forEach(key => {
      const lower = key.toLowerCase();
      if (lower.includes('hibid.com') || lower.includes('/extensions/image-proxy')) {
        delete map[key];
        changed = true;
      }
    });
    if (changed) {
      saveImageArchive(map);
    }
  } catch (err) {
    // Silent fail - pruning is a cleanup operation
  }
}

/**
 * Mark portal as ready and expose necessary global utilities
 */
export function markPortalReady(utilities: Record<string, any>): void {
  try {
    (window as any).portalReady = true;
    // Force bundler to keep these functions by attaching to window
    (window as any).__lotlingo_keep = utilities;
  } catch (err) {
    log.warn('Failed to mark portal ready:', err);
  }
}

/**
 * Check if document is ready and execute callback immediately or on DOMContentLoaded
 */
export function onDocumentReady(callback: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}
